/**
 * The editor's document and its history (10-web-admin § 6.3, § 6.6).
 *
 * The document is the thing autosave sends; history is in memory only. The
 * `409` path is § 6.6's: no merge in v1, so a stale revision blocks with a
 * dialog that offers a reload or a JSON copy — it never silently picks a
 * winner between two tabs.
 */

import { create } from 'zustand';
import { HISTORY_LIMIT, type EditorObject } from '@cowoo/shared';

export interface EditorDocument {
  pxPerCm: number;
  gridCm: number;
  backgroundOpacity: number;
  placements: EditorObject[];
}

export type SaveState =
  | { status: 'clean'; savedAt: number | null }
  | { status: 'dirty' }
  | { status: 'saving' }
  | { status: 'failed'; attempt: number }
  /** § 6.6 — someone else saved a newer `rev`; the user must choose. */
  | { status: 'conflict'; theirRev: number };

interface EditorState {
  versionId: string | null;
  rev: number;
  document: EditorDocument;
  selection: string[];
  save: SaveState;
  past: EditorDocument[];
  future: EditorDocument[];
  /**
   * § 6.3 — undo works in gestures, not frames: a drag or a resize applies a
   * change per animation frame, and each of those is not an "edit". Between
   * `beginGesture` and `endGesture` only the first apply lands in history.
   */
  gesture: 'none' | 'open' | 'recorded';
  beginGesture(): void;
  endGesture(): void;
  /**
   * § 6.2 "Einrasten" — a preference, not part of the document: the upload
   * step sets it and the editor honours it, and nothing on the server wants
   * to know.
   */
  snap: boolean;
  setSnap(next: boolean): void;

  load(versionId: string, rev: number, document: EditorDocument): void;
  /** Every mutation goes through here so history and the dirty flag stay true. */
  apply(next: (document: EditorDocument) => EditorDocument): void;
  select(ids: string[]): void;
  undo(): void;
  redo(): void;
  markSaving(): void;
  markSaved(rev: number, at: number): void;
  markFailed(): void;
  markConflict(theirRev: number): void;
  /** D-114 — this version wins: it is saved over the other writer's `rev`. */
  keepMine(): void;
  /** § 6.6 — the JSON the conflict dialog hands to the user before reloading. */
  exportDocument(): string;
}

const EMPTY: EditorDocument = { pxPerCm: 1, gridCm: 25, backgroundOpacity: 0.6, placements: [] };

export const useEditorStore = create<EditorState>((set, get) => ({
  versionId: null,
  rev: 0,
  document: EMPTY,
  selection: [],
  save: { status: 'clean', savedAt: null },
  past: [],
  future: [],
  gesture: 'none',
  beginGesture: () => set({ gesture: 'open' }),
  endGesture: () => set({ gesture: 'none' }),
  snap: true,
  setSnap: (next) => set({ snap: next }),

  load: (versionId, rev, document) =>
    set({
      versionId,
      rev,
      document,
      selection: [],
      save: { status: 'clean', savedAt: null },
      past: [],
      future: [],
    }),

  apply: (next) =>
    set((state) => ({
      document: next(state.document),
      // A new edit discards the redo branch, as every editor does. Inside a
      // gesture the first frame is the edit; the following ones refine it.
      past: state.gesture === 'recorded' ? state.past : [...state.past, state.document].slice(-HISTORY_LIMIT),
      future: [],
      gesture: state.gesture === 'open' ? 'recorded' : state.gesture,
      save: { status: 'dirty' },
    })),

  select: (selection) => set({ selection }),

  undo: () =>
    set((state) => {
      const previous = state.past.at(-1);
      if (!previous) return state;
      return {
        document: previous,
        past: state.past.slice(0, -1),
        future: [state.document, ...state.future].slice(0, HISTORY_LIMIT),
        save: { status: 'dirty' },
      };
    }),

  redo: () =>
    set((state) => {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        document: next,
        past: [...state.past, state.document].slice(-HISTORY_LIMIT),
        future: rest,
        save: { status: 'dirty' },
      };
    }),

  markSaving: () => set({ save: { status: 'saving' } }),

  markSaved: (rev, at) => set({ rev, save: { status: 'clean', savedAt: at } }),

  markFailed: () =>
    set((state) => ({
      // Edits continue locally; the retry backoff is the caller's.
      save: { status: 'failed', attempt: state.save.status === 'failed' ? state.save.attempt + 1 : 1 },
    })),

  markConflict: (theirRev) => set({ save: { status: 'conflict', theirRev } }),
  keepMine: () =>
    set((state) =>
      state.save.status === 'conflict' ? { rev: state.save.theirRev, save: { status: 'dirty' } } : {},
    ),

  exportDocument: () => JSON.stringify({ rev: get().rev, document: get().document }, null, 2),
}));

/** § 6.6 — autosave fires this long after the last change. */
export const AUTOSAVE_DEBOUNCE_MS = 1500;
/** Retry backoff after a failed save. */
export const SAVE_BACKOFF_MS = [2000, 5000, 15_000];

export function backoffFor(attempt: number): number {
  return SAVE_BACKOFF_MS[Math.min(attempt, SAVE_BACKOFF_MS.length - 1)] ?? 15_000;
}

/** A dirty document must warn before the tab closes (§ 6.6 rule 4). */
export function shouldWarnOnUnload(save: SaveState): boolean {
  return save.status === 'dirty' || save.status === 'saving' || save.status === 'failed';
}
